import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/cms/client/generated-form.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import { DateTimeAspectSchema } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import '@chancenwerk/client/components/chancen-component-context-menu.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { FamilyEvent } from '@chancenwerk/family/entities/FamilyEvent.js';
import { ChancenPersonalConfigAPI } from '@chancenwerk/shared/user-configs/api/ChancenPersonalConfigAPI.js';
import { ListUIConfig } from '@chancenwerk/shared/user-configs/entities/ListUIConfig.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';

const filterSelector: BaseQLSelectionSet<AdornisFilter> = {
  ...AdornisFilter.allFields,
  aspects: { ...AbstractAspect.allFields, value: 1, zipCity: { zip: 1, city: 1 } },
};

/**
 * @customElement family-page-overview
 */
@customElement('family-page-overview')
export class FamilyPageOverview extends CrPage {
  @state() _view = new View({ filter: new AdornisFilter({ aspects: [] }) });

  @state() private readonly _config = new RXController(
    this,
    ChancenPersonalConfigAPI.getByKeyAndCategory(
      ListUIConfig<typeof FamilyEvent>,
      'list-family-events',
      'default',
    )({
      key: 1,
      userID: 1,
      category: 1,
      view: {
        filter: filterSelector,
        mode: 1,
        configuredColumns: { customID: 1, fieldName: 1, columnWidth: 1 },
        selectedItems: 1,
        multiSelection: 1,
      },
    }),
  );

  constructor() {
    super();

    this.label = 'Übersicht';
    this.description = 'Übersicht über alle Termine';
    this.permission = ChancenPermission.family.LOGIN_FAMILY;
  }

  override header() {
    return html`<chancen-component-context-menu
      .items=${[
        {
          icon: 'add',
          label: 'Hinzufügen',
          action: async () => {
            ChromiumDialog.showPopup<void>({
              content: resolve => {
                const form = new GenericFieldFormController(this, new FamilyEvent({}), {
                  onSuccess: async doc => {
                    await doc.create();
                    resolve();
                  },
                });

                return html`<cr-flex ${css({ width: '400px' })} padding="md" space="md">
                  <cr-input placeholder="Name" ${form.field('name')}></cr-input>
                  <cr-input placeholder="öffentlicher Name" ${form.field('publicName')}></cr-input>
                  <cr-date-picker-dropdown
                    .end-year=${DateTime.now().plus({ years: 5 }).year}
                    placeholder="wann?"
                    click-to-save
                    flex
                    clearable
                    show-time
                    ${form.field('when')}
                  ></cr-date-picker-dropdown>
                  <cr-button @click=${() => form.validate()}>Speichern</cr-button>
                </cr-flex>`;
              },
            });
          },
        },
      ]}
    ></chancen-component-context-menu>`;
  }

  override content() {
    return html`${this._overview()}`;
  }

  private _overview() {
    if (this._config.isLoading) {
      return html`<cr-loader></cr-loader>`;
    }

    const options = buildChancenDataViewOptions<typeof FamilyEvent>(
      {
        class: FamilyEvent,
        itemSpacing: this.spacing.md,
        defaultFieldMapping: { DateTime: (value: DateTime) => value.toFormat('dd.MM.yyyy, HH:mm') },
        view: this._config.value?.view ?? new View({ filter: new AdornisFilter({}) }),
        onViewChanged: async view => {
          if (!this._config.value) return;
          this._config.value.view = view;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
        rowHref: (entityInstance: FamilyEvent) => {
          return `/event/${entityInstance._id}`;
        },
        isColumnResizable: true,
        isColumnSelectable: true,
        isColumnResortable: true,
        fieldSelectionOptions: {
          hideFieldsWithoutTranslation: true,
          shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({ blacklist: { registrations: 1 } }),
        },
        onlyShowLastTranslation: true,
        customColumns: [],
      } as DataViewOptions<typeof FamilyEvent>,
      {
        fieldAspectGeneration: {
          hideFieldsWithoutTranslation: true,
          blacklist: { registrations: 1, acceptedCount: 1 },
        },
        overridesForFieldName: { when: DateTimeAspectSchema('range') },
        onFilterChanged: async filter => {
          if (!this._config.value?.view) return;
          this._config.value.view.filter = filter;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
      },
    );

    return html`
      <cr-card padding="sm" ${css({ flexShrink: '0', position: 'sticky', left: 0 })} margin="sm">
        ${FilterEditor({ options: options.filterOptions })}
      </cr-card>
      <cr-flex ${css({ overflow: 'auto' })}>
        <cr-data-view ${css({ marginTop: this.spacing.sm })} .options=${options.dataViewOptions}> </cr-data-view>
      </cr-flex>
    `;
  }
}
